import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  AppContainer,
  MainContentContainer,
  BodyContainer,
} from '../../resources/styling/appStyle';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import {
  Grid,
  Paper,
  InputBase,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Stack } from '@mui/system';
import { TablePagination, CircularProgress, Typography } from '@mui/material';

import superAdminService from '../../services/api/superAdminService';
import { routesObj } from '../../routes';
import { DateFormat } from '../../utils/CommonFunctions';
import Autocomplete from './Autocomplete';
import PlatformAiUsageSection from '../../components/aiUsage/PlatformAiUsageSection';
import AccountTabs from '../../components/account/AccountTabs';
import { sa, DataTable } from '../../components/superAdminUi';

// ---------------------------------------------------------------------------
// Surveys-tab presentation, transcribed from the approved Figma. Purely visual:
// the stat cards, navigation tool cards, and Latest Survey table wrap the exact
// same data + handlers that already drove the tab.
// ---------------------------------------------------------------------------

// Accent per survey-count card (Welbee figure + underline share the colour).
const SURVEY_ACCENT = {
  created: '#e84f6b',
  live: '#22b07d',
  scheduled: '#f5a623',
  completed: '#20ace9',
};

// Audience pill colours, matching the badge palette in the design.
const AUDIENCE_COLOR = {
  Staff: '#20ace9',
  Parent: '#e84f6b',
  Student: '#f5a623',
};

const SurveysWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
  width: 100%;
  font-family: ${sa.font.family};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: ${sa.color.white};
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.lg};
  border-bottom: 3px solid ${(p) => p.$accent};
  padding: 18px 22px 16px;
`;

const StatTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 17px !important;
  font-weight: ${sa.font.bold};
  color: ${sa.color.text};
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  & + & {
    margin-top: 4px;
  }
`;

const StatLabel = styled.span`
  font-size: 13px;
  color: ${sa.color.textGray};
`;

const StatValue = styled.span`
  font-size: 15px;
  font-weight: ${sa.font.bold};
  color: ${(p) => p.$color || sa.color.text};
`;

const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-areas:
    'groups schools questions'
    'groups schools templates';
  gap: 16px;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'groups'
      'schools'
      'questions'
      'templates';
  }
`;

const ToolCard = styled.div`
  position: relative;
  grid-area: ${(p) => p.$area};
  background: ${(p) => p.$bg};
  border-left: 4px solid ${(p) => p.$accent};
  border-radius: ${sa.radius.md};
  padding: 22px 24px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: ${(p) => (p.$tall ? 'space-between' : 'flex-start')};
  min-height: ${(p) => (p.$tall ? '220px' : '102px')};
  transition: box-shadow 0.15s ease-in-out;

  &:hover {
    box-shadow: ${sa.shadow.card};
  }
`;

const ToolTitle = styled.h3`
  margin: 0;
  font-size: 18px !important;
  font-weight: ${sa.font.bold};
  color: ${sa.color.text};  
`;

const ToolText = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: ${sa.color.textGray};
  max-width: 92%;
`;

const IconBadge = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: ${(p) => p.$color};
  color: ${sa.color.white};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LatestTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: ${sa.font.bold};
  color: ${sa.color.text};
`;

const SubTabs = styled.div`
  display: flex;
  gap: 5px;
  margin: 14px 0 18px;
`;

const SubTab = styled.button`
  border: 1px solid ${(p) => (p.$active ? sa.color.blue : sa.color.stroke)};
  background: ${(p) => (p.$active ? sa.color.blue : sa.color.white)};
  color: ${(p) => (p.$active ? sa.color.white : sa.color.textGray)};
  border-radius: 8px;
  padding: 8px 20px;
  font-family: ${sa.font.family};
  font-size: 13px;
  font-weight: ${sa.font.semibold};
  cursor: pointer;
`;

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
`;

const SortArrow = styled.span`
  font-size: 11px;
  line-height: 1;
`;

const RowName = styled.span`
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
`;

const AudiencePill = styled.span`
  display: inline-block;
  background: ${(p) => p.$bg};
  color: ${sa.color.white};
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: ${sa.font.semibold};
`;

const PaginationBar = styled.div`
  margin-top: 8px;
`;

const CenterSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

// Audience code → the pills the design shows in the Audience(s) column.
const AUDIENCE_LABELS = {
  1: ['Staff'],
  2: ['Parent'],
  3: ['Staff', 'Parent'],
  4: ['Student'],
  5: ['Staff', 'Student'],
  6: ['Parent', 'Student'],
  7: ['Staff', 'Parent', 'Student'],
};

const renderAudiencePills = (audience) => {
  const labels = AUDIENCE_LABELS[audience] || [];
  return (
    <Stack direction='row' spacing={0.5}>
      {labels.map((label) => (
        <AudiencePill key={label} $bg={AUDIENCE_COLOR[label]}>
          {label}
        </AudiencePill>
      ))}
    </Stack>
  );
};

function SuperAdminDashboard() {
  const [value, setValue] = React.useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
    // Reset pagination to first page when switching between Welbee and Insights tabs
    setPage(0);
    // Reset filters when switching tabs
    if (newValue === 1) {
      // Insights tab - use startDate desc as default
      setOrderBy('startDate');
      setOrder('desc');
      setStatusFilter('all');
      setSearchTerm('');
      setAudienceFilter(0);
      setIsCheckInFilter(false);
      setShowTestingSurveys(false);
      // Load Insights surveys with default sorting
      loadSurveys('Insights', 1, rowsPerPage, 'startDate', 'desc', '', 'all');
    } else {
      // Welbee tab
      setOrderBy('Id');
      setOrder('desc');
      loadSurveys('Welbee', 1, rowsPerPage, 'Id', 'desc');
    }
  };

  const navigate = useHistory();
  const [adminDashboardInfo, setAdminDashboardInfo] = useState({});
  const [surveys, setSurveys] = React.useState([]);
  const [mysurvey, setMysurvey] = React.useState([]);
  const [filteredSurveys, setFilteredSurveys] = React.useState([]);
  //const [loading, setLoading] = React.useState(false)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surveysLoading, setSurveysLoading] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('desc'); // 'asc' or 'desc'
  const [orderBy, setOrderBy] = useState('Id'); // Default column to sort by (Id for Welbee, startDate for Insights)
  const [totalSurveysCount, setTotalSurveysCount] = useState(0);
  const [allSchoolsMatsList, setAllSchoolsMatsList] = useState([]);
  const [insightsCounts, setInsightsCounts] = useState({
    live: 0,
    scheduled: 0,
    completed: 0,
    created: 0
  });
  const [statusFilter, setStatusFilter] = useState('all'); // Status filter for Insights surveys
  const [searchTerm, setSearchTerm] = useState(''); // Search term for Insights surveys
  const [audienceFilter, setAudienceFilter] = useState(0); // Audience filter (0 = all)
  const [isCheckInFilter, setIsCheckInFilter] = useState(false); // Check-in survey filter
  const [showTestingSurveys, setShowTestingSurveys] = useState(false); // Testing surveys filter

  // Helper function to map status string to status number
  // Maps UI status strings to backend SurveyStatus enum values:
  // DRAFT = 1, PUBLISHED = 2, RUNNING = 3, CLOSED = 4, CANCLED = 5
  const getStatusNumber = (statusString) => {
    const statusMap = {
      'draft': 1,        // DRAFT = 1
      'published': 2,    // PUBLISHED = 2
      'live': 3,         // RUNNING = 3 (UI uses 'live' for running surveys)
      'running': 3,      // RUNNING = 3
      'closed': 4,       // CLOSED = 4
      'cancelled': 5,    // CANCLED = 5
      'canceled': 5      // CANCLED = 5 (alternative spelling)
    };
    return statusMap[statusString?.toLowerCase()] || null;
  };

  // Load Insights survey counts on mount using dedicated counts API
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await superAdminService.getInsightsSurveyCounts();
        
        if (response.success && response.data) {
          // Map API response to expected format
          // API returns: { running, draft, published, closed, total }
          const counts = {
            live: response.data.running || response.data.live || response.data.RUNNING || 0,
            scheduled: (response.data.draft || 0) + (response.data.published || 0) || 
                      response.data.scheduled || (response.data.DRAFT || 0) + (response.data.PUBLISHED || 0),
            completed: response.data.closed || response.data.completed || response.data.CLOSED || 0,
            created: response.data.total || response.data.created || response.data.totalCount || 0
          };
          
          setInsightsCounts(counts);
          
          // Update dashboard info with Insights counts
          setAdminDashboardInfo(prev => ({
            ...prev,
            InsightsLiveSurveyCounts: counts.live,
            InsightsScheduledSurveyCounts: counts.scheduled,
            InsightsCompletedSurveyCounts: counts.completed,
            InsightsCreatedSurveyCounts: counts.created
          }));
        }
      } catch (err) {
        console.error('Error loading Insights survey counts:', err);
      }
    };
    
    fetchCounts();
  }, []);

  // Function to load surveys - use server-side pagination for both Insights and Welbee
  const loadSurveys = (
    surveyType,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    searchTermParam = '',
    statusFilterParam = null,
    showTestingSurveysParam = null,
    isCheckInFilterParam = null,
    audienceFilterParam = null
  ) => {
    setSurveysLoading(true);
    
    if (surveyType === 'Insights') {
      // Use server-side pagination for Insights surveys
      const currentStatusFilter = statusFilterParam !== null ? statusFilterParam : statusFilter;
      const currentSearchTerm = searchTermParam !== undefined && searchTermParam !== null 
        ? String(searchTermParam).trim() 
        : String(searchTerm || '').trim();
      const currentAudience =
        audienceFilterParam !== null && audienceFilterParam !== undefined
          ? audienceFilterParam
          : audienceFilter;
      
      // Map status filter to statuses array
      const statuses = [];
      if (currentStatusFilter && currentStatusFilter !== 'all' && currentStatusFilter !== '') {
        const statusNumber = getStatusNumber(currentStatusFilter);
        if (statusNumber) {
          statuses.push(statusNumber);
        }
      }
      
      // Map sortBy to API format (API may handle sorting server-side, but we'll pass it for now)
      // Note: The API may not support all sort fields, so backend sorting might be limited
      
      // Build search params - only include filters that are actually set
      // API uses 1-based page numbers (must be > 0)
      const searchParams = {
        pageNumber: pageNumber, // Frontend is already 1-based
        pageSize: pageSize
      };
      
      // Only add searchTerm if it's not empty
      if (currentSearchTerm && currentSearchTerm.length > 0) {
        searchParams.searchTerm = currentSearchTerm;
      }
      
      // Only add statuses if a status filter is selected
      if (statuses.length > 0) {
        searchParams.statuses = statuses;
      }
      
      // Add audience filter if selected
      if (currentAudience && currentAudience > 0) {
        searchParams.audience = currentAudience;
      }
      
      // Only send check-in filter when checked (true)
      // Use parameter if provided (for immediate updates), otherwise use state
      const currentCheckInFilter = isCheckInFilterParam !== null ? isCheckInFilterParam : isCheckInFilter;
      if (currentCheckInFilter) {
        searchParams.isCheckInSurvey = true;
      }

      // Always pass explicit boolean for testing surveys filter
      // Backend handles filtering and pagination correctly
      // Use parameter if provided (for immediate updates), otherwise use state
      const currentShowTesting = showTestingSurveysParam !== null ? showTestingSurveysParam : showTestingSurveys;
      searchParams.showTestingSurveys = currentShowTesting;
      
      superAdminService
        .searchInsightsSurveys(searchParams)
        .then((response) => {
          if (response.success && response.data) {
            // Map Insights data to match expected format (matching table field names)
            // Backend handles all filtering and pagination, so use data directly
            const mappedData = response.data.map(s => {
              // When matName is not null, show matName in both School/Trust Name and Group columns
              // When matName is null, show schoolName in School/Trust Name column
              const schoolTrustName = s.matName ? s.matName : (s.schoolName || '');
              const groupName = s.matName ? s.matName : (s.schoolName || '');
              
              return {
                Id: s.id,
                Name: s.name || '',
                StartDateTime: s.startDate ? new Date(s.startDate).toISOString() : new Date(0).toISOString(),
                EndDateTime: s.endDate ? new Date(s.endDate).toISOString() : null,
                SchoolName: schoolTrustName, // School/Trust Name: matName if present, otherwise schoolName
                MatName: groupName, // Group: matName if present, otherwise schoolName
                Audience: s.audience, // Capitalized to match table
                Status: s.status || '', // Capitalized to match table
                startDate: s.startDate, // lowercase to match DateFormat usage
                endDate: s.endDate // lowercase to match DateFormat usage
              };
            });
            
            setSurveys(mappedData);
            // Use totalCount directly from API response - backend handles filtering
            setTotalSurveysCount(response.totalCount || 0);
            setSurveysLoading(false);
          } else {
            setSurveys([]);
            setTotalSurveysCount(0);
            setSurveysLoading(false);
          }
        })
        .catch((err) => {
          console.error('Error loading Insights surveys:', err);
          setSurveys([]);
          setTotalSurveysCount(0);
          setSurveysLoading(false);
        });
    } else {
      // For Welbee surveys, use backend pagination
      superAdminService
        .getSurveysPaginated(surveyType, pageNumber, pageSize, sortBy, sortDirection, searchTerm)
        .then((response) => {
          if (response.success && response.data) {
            setSurveys(response.data);
            setTotalSurveysCount(response.total || 0);
            setSurveysLoading(false);
          } else {
            setSurveys([]);
            setTotalSurveysCount(0);
            setSurveysLoading(false);
          }
        })
        .catch((err) => {
          console.error('Error loading surveys:', err);
          setSurveys([]);
          setTotalSurveysCount(0);
          setSurveysLoading(false);
        });
    }
  };

  useEffect(() => {
    setLoading(true);
    // Use optimized dashboard summary endpoint
    superAdminService
      .getDashboardSummary()
      .then((response) => {
        if (response.success) {
          // Merge backend summary with Insights counts from state (calculated from frontend API)
          // Use current insightsCounts state value
          setAdminDashboardInfo(prev => ({
            ...response.data,
            // Preserve Insights counts from frontend API call (stored in insightsCounts state)
            // If counts aren't loaded yet, they'll be 0 and will be updated when Insights API completes
            InsightsLiveSurveyCounts: insightsCounts.live || prev.InsightsLiveSurveyCounts || 0,
            InsightsScheduledSurveyCounts: insightsCounts.scheduled || prev.InsightsScheduledSurveyCounts || 0,
            InsightsCompletedSurveyCounts: insightsCounts.completed || prev.InsightsCompletedSurveyCounts || 0,
            InsightsCreatedSurveyCounts: insightsCounts.created || prev.InsightsCreatedSurveyCounts || 0
          }));
          
          // Note: allSchoolsList and allMatsList are no longer returned in summary
          // They will be loaded on-demand via search/autocomplete
          setAllSchoolsMatsList([]);
          setLoading(false);
          // Load initial surveys for Welbee tab (value === 0)
          loadSurveys('Welbee', 1, rowsPerPage, orderBy, order);
        }
      })
      .catch((err) => {
        console.error('Error loading dashboard:', err);
        setLoading(false);
      });
  }, []); // Only run once on mount

  // Load Insights surveys when Insights tab is selected
  useEffect(() => {
    if (value === 1 && surveys.length === 0) {
      // Load Insights surveys when tab is active and no surveys are displayed
      loadSurveys('Insights', 1, rowsPerPage, 'startDate', 'desc', '', 'all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // Trigger when tab changes

  //  // Filter options based on input value
  //  useEffect(() => {
  //   if (inputValue) {
  //     const filtered = options.filter(option =>
  //       option.name.toLowerCase().includes(inputValue.toLowerCase())
  //     );
  //     setFilteredOptions(filtered);
  //   } else {
  //     setFilteredOptions([]);
  //   }
  // }, [inputValue, options]);

  // const handleInputChange = (event) => {
  //   setInputValue(event.target.value);
  // };

  // const handleOptionClick = (option) => {
  //   setInputValue(option.Name);
  //   setFilteredOptions([]);
  // };

  /* Welbee Grid Properties and function starts*/
  const handleWelbeeChangePage = (event, newPage) => {
    setPage(newPage);
    loadSurveys('Welbee', newPage + 1, rowsPerPage, orderBy, order);
  };
  const handleWelbeeChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadSurveys('Welbee', 1, newRowsPerPage, orderBy, order);
  };
  const handleWelbeeSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    loadSurveys('Welbee', page + 1, rowsPerPage, property, newOrder);
  };
  // Use surveys from server-side pagination
  const sortedWelbeeData = surveys || [];
  /* Welbee Grid Properties and function ends*/

  /* Insights Grid Properties and function starts*/
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadSurveys('Insights', newPage + 1, rowsPerPage, orderBy, order, searchTerm, statusFilter);
  };
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadSurveys('Insights', 1, newRowsPerPage, orderBy, order, searchTerm, statusFilter);
  };
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    loadSurveys('Insights', page + 1, rowsPerPage, property, newOrder, searchTerm, statusFilter);
  };
  const handleStatusFilterChange = (event) => {
    const newStatus = event.target.value;
    setStatusFilter(newStatus);
    setPage(0);
    // Pass both searchTerm and newStatus to ensure filters are applied correctly
    loadSurveys('Insights', 1, rowsPerPage, orderBy, order, searchTerm, newStatus);
  };
  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    // Only trigger search if 3 or more characters, or if clearing the search (empty)
    if (newSearchTerm.length === 0 || newSearchTerm.length >= 3) {
      setPage(0);
      // Pass both newSearchTerm and current statusFilter to ensure filters are applied correctly
      loadSurveys('Insights', 1, rowsPerPage, orderBy, order, newSearchTerm, statusFilter);
    }
  };
  
  // Handler for audience filter
  const handleAudienceFilterChange = (event) => {
    const newAudience = parseInt(event.target.value, 10) || 0;
    setAudienceFilter(newAudience);
    setPage(0);
    // Pass newAudience directly to avoid stale state issues (searchTerm, statusFilter, showTestingSurveys, isCheckIn, audience)
    loadSurveys(
      'Insights',
      1,
      rowsPerPage,
      orderBy,
      order,
      searchTerm,
      statusFilter,
      null,
      null,
      newAudience
    );
  };
  
  // Handler for check-in filter
  const handleCheckInFilterChange = (event) => {
    const newValue = event.target.checked;
    setIsCheckInFilter(newValue);
    setPage(0);
    // Pass newValue directly to avoid stale state issue
    loadSurveys('Insights', 1, rowsPerPage, orderBy, order, searchTerm, statusFilter, null, newValue);
  };

  const handleTestingSurveysFilterChange = (event) => {
    const newValue = event.target.checked;
    setShowTestingSurveys(newValue);
    setPage(0);
    // Pass newValue directly to avoid stale state issue (showTestingSurveysParam, isCheckInFilterParam)
    loadSurveys('Insights', 1, rowsPerPage, orderBy, order, searchTerm, statusFilter, newValue, null);
  };
  
  // Use surveys from server-side pagination
  const sortedData = surveys || [];
  /* Insights Grid Properties and function ends*/

  if (error) {
    return (
      <Typography variant='h6' color='error' align='center'>
        {error}
      </Typography>
    );
  }

  // Sortable purple-header cell: click toggles the same sort handlers as before,
  // the arrow shows the active column's direction.
  const sortHeader = (label, column, onSort) => (
    <SortButton type='button' onClick={() => onSort(column)}>
      {label}
      {orderBy === column && (
        <SortArrow>{order === 'asc' ? '↑' : '↓'}</SortArrow>
      )}
    </SortButton>
  );

  const welbeeColumns = [
    {
      key: 'SchoolName',
      width: '26%',
      header: sortHeader('School/Trust Name', 'SchoolName', handleWelbeeSort),
      render: (row) => <RowName>{row.SchoolName}</RowName>,
    },
    {
      key: 'Name',
      header: sortHeader('Survey Name', 'Name', handleWelbeeSort),
      render: (row) => row.Name,
    },
    {
      key: 'Audience',
      width: '14%',
      header: sortHeader('Audience', 'Audience', handleWelbeeSort),
      render: () => (
        <AudiencePill $bg={AUDIENCE_COLOR.Staff}>Staff</AudiencePill>
      ),
    },
    {
      key: 'CreatedAt',
      width: '16%',
      header: sortHeader('Launch Date', 'CreatedAt', handleWelbeeSort),
      render: (row) => DateFormat(row.StartDateTime, 'MM/DD/YYYY'),
    },
    {
      key: 'EndDateTime',
      width: '16%',
      header: sortHeader('Close Date', 'EndDateTime', handleWelbeeSort),
      render: (row) => DateFormat(row.EndDateTime, 'MM/DD/YYYY'),
    },
  ];

  const insightsColumns = [
    {
      key: 'SchoolName',
      width: '20%',
      header: sortHeader('School/Trust Name', 'SchoolName', handleSort),
      render: (row) => <RowName>{row.SchoolName}</RowName>,
    },
    {
      key: 'Name',
      header: sortHeader('Survey Name', 'Name', handleSort),
      render: (row) => row.Name,
    },
    {
      key: 'startDate',
      width: '13%',
      header: sortHeader('Launch Date', 'startDate', handleSort),
      render: (row) => DateFormat(row.startDate, 'MM/DD/YYYY'),
    },
    {
      key: 'endDate',
      width: '13%',
      header: sortHeader('Close Date', 'endDate', handleSort),
      render: (row) => DateFormat(row.endDate, 'MM/DD/YYYY'),
    },
    {
      key: 'Status',
      width: '11%',
      header: sortHeader('Status', 'Status', handleSort),
      render: (row) => row.Status,
    },
    {
      key: 'Audience',
      width: '14%',
      header: 'Audience(s)',
      render: (row) => renderAudiencePills(row.Audience),
    },
    {
      key: 'MatName',
      header: 'Group',
      render: (row) => row.MatName || '',
    },
  ];

  // The Surveys tab keeps the full survey experience: overview stat cards,
  // navigation tools, and the Latest Survey table with its Welbee/Insights
  // sub-tabs, behind a single tab so the home reads as tabs, not a long scroll.
  const surveysTabContent = (
    <SurveysWrap>
      {/* Survey count overview */}
      <StatGrid>
        <StatCard $accent={SURVEY_ACCENT.created}>
          <StatTitle>Survey Created</StatTitle>
          <StatRow>
            <StatLabel>Welbee:</StatLabel>
            <StatValue $color={SURVEY_ACCENT.created}>
              {adminDashboardInfo.CreatedSurveyCounts}
            </StatValue>
          </StatRow>
          <StatRow>
            <StatLabel>Insights:</StatLabel>
            <StatValue>
              {adminDashboardInfo.InsightsCreatedSurveyCounts || 0}
            </StatValue>
          </StatRow>
        </StatCard>

        <StatCard $accent={SURVEY_ACCENT.live}>
          <StatTitle>Live Survey</StatTitle>
          <StatRow>
            <StatLabel>Welbee:</StatLabel>
            <StatValue $color={SURVEY_ACCENT.live}>
              {adminDashboardInfo.LiveSurveyCounts}
            </StatValue>
          </StatRow>
          <StatRow>
            <StatLabel>Insights:</StatLabel>
            <StatValue>
              {adminDashboardInfo.InsightsLiveSurveyCounts || 0}
            </StatValue>
          </StatRow>
        </StatCard>

        <StatCard $accent={SURVEY_ACCENT.scheduled}>
          <StatTitle>Scheduled Survey</StatTitle>
          <StatRow>
            <StatLabel>Welbee:</StatLabel>
            <StatValue $color={SURVEY_ACCENT.scheduled}>
              {adminDashboardInfo.ScheduledSurveyCounts}
            </StatValue>
          </StatRow>
          <StatRow>
            <StatLabel>Insights:</StatLabel>
            <StatValue>
              {adminDashboardInfo.InsightsScheduledSurveyCounts || 0}
            </StatValue>
          </StatRow>
        </StatCard>

        <StatCard $accent={SURVEY_ACCENT.completed}>
          <StatTitle>Completed Survey</StatTitle>
          <StatRow>
            <StatLabel>Welbee:</StatLabel>
            <StatValue $color={SURVEY_ACCENT.completed}>
              {adminDashboardInfo.CompletedSurveyCounts}
            </StatValue>
          </StatRow>
          <StatRow>
            <StatLabel>Insights:</StatLabel>
            <StatValue>
              {adminDashboardInfo.InsightsCompletedSurveyCounts || 0}
            </StatValue>
          </StatRow>
        </StatCard>
      </StatGrid>

      {/* Navigation / tool cards */}
      <ToolsGrid>
        <ToolCard
          $area='groups'
          $tall
          $bg='#d8effb'
          $accent='#4aa6d6'
          onClick={() => navigate.push(routesObj.GroupListing.path)}
        >
          <IconBadge $color='#4aa6d6'>
            <GroupsIcon fontSize='small' />
          </IconBadge>
          <ToolTitle>Groups</ToolTitle>
          <ToolText>
            Manage large clusters of schools and educational institutions
            under one umbrella.
          </ToolText>
        </ToolCard>

        <ToolCard
          $area='schools'
          $tall
          $bg='#f8f0d6'
          $accent='#e0a500'
          onClick={() => navigate.push(routesObj.SchoolListing.path)}
        >
          <IconBadge $color='#e0a500'>
            <ApartmentIcon fontSize='small' />
          </IconBadge>
          <ToolTitle>Individual Schools</ToolTitle>
          <ToolText>
            Quick access to specific school metrics and direct platform
            management tools.
          </ToolText>
        </ToolCard>

        <ToolCard
          $area='questions'
          $bg='#cfe8e2'
          $accent={sa.color.teal}
          onClick={() => navigate.push(routesObj.InsightsQuestionBank.path)}
        >
          <IconBadge $color={sa.color.teal}>
            <HelpOutlineIcon fontSize='small' />
          </IconBadge>
          <ToolTitle>Insight Questions</ToolTitle>
          <ToolText>
            Configure targeted questionnaire sets to gather deeper
            intelligence from your audience.
          </ToolText>
        </ToolCard>

        <ToolCard
          $area='templates'
          $bg='#f0e2fb'
          $accent='#8b5cf6'
          onClick={() =>
            navigate.push(routesObj.InsightsSurveyTemplates.path)
          }
        >
          <IconBadge $color='#8b5cf6'>
            <DescriptionOutlinedIcon fontSize='small' />
          </IconBadge>
          <ToolTitle>Insights Survey Templates</ToolTitle>
          <ToolText>
            Pre-built verified frameworks for common educational measurement
            scenarios.
          </ToolText>
        </ToolCard>
      </ToolsGrid>

      {/* Latest Survey */}
      <div>
        <LatestTitle>Latest Survey</LatestTitle>
        <SubTabs>
          <SubTab
            type='button'
            aria-pressed={value === 0}
            $active={value === 0}
            onClick={() => handleChange(null, 0)}
          >
            Welbee
          </SubTab>
          <SubTab
            type='button'
            aria-pressed={value === 1}
            $active={value === 1}
            onClick={() => handleChange(null, 1)}
          >
            Insights
          </SubTab>
        </SubTabs>

        {loading === true ? (
          <CenterSpinner>
            <CircularProgress />
          </CenterSpinner>
        ) : value === 0 ? (
          <>
            {surveysLoading && (
              <CenterSpinner>
                <CircularProgress size={24} />
              </CenterSpinner>
            )}
            <DataTable
              columns={welbeeColumns}
              rows={sortedWelbeeData}
              getRowKey={(row) => row.Id}              
              // pageSize={10}
              // size='sm'
              empty='No surveys to display.'
            />
            
            <PaginationBar>
              <TablePagination
                rowsPerPageOptions={[10, 20, 30]}
                component='div'
                count={adminDashboardInfo.CreatedSurveyCounts || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleWelbeeChangePage}
                onRowsPerPageChange={handleWelbeeChangeRowsPerPage}
              />
            </PaginationBar>
          </>
        ) : (
          <>
            {/* Search and Filters */}
            <Stack
              direction='row'
              spacing={2}
              sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Paper
                component='form'
                sx={{                  
                  display: 'flex',
                  alignItems: 'center',
                  width: 300,
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                }}
                elevation={0}
                onSubmit={(e) => {
                  e.preventDefault();
                  // Only search if 3 or more characters, or if empty
                  if (searchTerm.length === 0 || searchTerm.length >= 3) {
                    setPage(0);
                    loadSurveys('Insights', 1, rowsPerPage, orderBy, order, searchTerm, statusFilter);
                  }
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder='Search surveys...'
                  value={searchTerm}
                  onChange={handleSearchChange}
                  inputProps={{ 'aria-label': 'search surveys' }}
                />
                <IconButton type='submit' aria-label='search'>
                  <SearchIcon />
                </IconButton>
              </Paper>
              <FormControl sx={{ minWidth: 150 }} size='small'>
                <InputLabel id='status-filter-label'>Status</InputLabel>
                <Select
                  labelId='status-filter-label'
                  id='status-filter'
                  value={statusFilter}
                  label='Status'
                  onChange={handleStatusFilterChange}
                  sx={{ backgroundColor: 'white' }}
                  renderValue={(value) => {
                    if (value === 'all') return 'All';
                    if (value === 'live') return 'Live';
                    if (value === 'draft') return 'Draft';
                    if (value === 'closed') return 'Closed';
                    if (value === 'published') return 'Published';
                    return value;
                  }}
                >
                  <MenuItem value='all'>All</MenuItem>
                  <MenuItem value='live'>Live</MenuItem>
                  <MenuItem value='draft'>Draft</MenuItem>
                  <MenuItem value='closed'>Closed</MenuItem>
                  <MenuItem value='published'>Published</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150 }} size='small'>
                <InputLabel id='audience-filter-label'>Audience</InputLabel>
                <Select
                  labelId='audience-filter-label'
                  id='audience-filter'
                  value={audienceFilter}
                  label='Audience'
                  onChange={handleAudienceFilterChange}
                  sx={{ backgroundColor: 'white' }}
                >
                  <MenuItem value={0}>All</MenuItem>
                  <MenuItem value={1}>Staff</MenuItem>
                  <MenuItem value={2}>Parents</MenuItem>
                  <MenuItem value={3}>Staff &amp; Parents</MenuItem>
                  <MenuItem value={4}>Pupils</MenuItem>
                  <MenuItem value={5}>Staff &amp; Students</MenuItem>
                  <MenuItem value={6}>Parents &amp; Students</MenuItem>
                  <MenuItem value={7}>Staff, Parents &amp; Students</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCheckInFilter}
                    onChange={handleCheckInFilterChange}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                  />
                }
                label='Check-in Surveys Only'                
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showTestingSurveys}
                    onChange={handleTestingSurveysFilterChange}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                  />
                }
                label='Show Testing Surveys'                
              />
            </Stack>
            {surveysLoading && (
              <CenterSpinner>
                <CircularProgress size={24} />
              </CenterSpinner>
            )}
            <DataTable
              columns={insightsColumns}
              rows={sortedData}
              getRowKey={(row) => row.Id}
              // pageSize={10}
              // size='sm'
              empty='No surveys to display.'
            />
            <PaginationBar>
              <TablePagination
                rowsPerPageOptions={[10, 20, 30]}
                component='div'
                count={totalSurveysCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </PaginationBar>
          </>
        )}
      </div>
    </SurveysWrap>
  );

  // Approved home layout: two tabs beneath the header + search. Platform AI usage
  // is the default view; the survey areas live behind the Surveys tab.
  const dashboardTabs = [
    {
      key: 'ai-usage',
      label: 'AI Usage / Platform Usage',
      content: <PlatformAiUsageSection />,
    },
    {
      key: 'surveys',
      label: 'Surveys',
      content: surveysTabContent,
    },
  ];

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          <Grid container mb={2}>
            <Grid item xs={12} lg={9} md={9} sm={9}>
              <h3>Dashboard</h3>
            </Grid>
            <Grid item xs={12} lg={3} md={3} sm={3}>
              <Paper elevation={0}>
                <Autocomplete />
              </Paper>
            </Grid>
          </Grid>

          <AccountTabs
            tabs={dashboardTabs}
            defaultTabKey='ai-usage'
            idBase='dashboard'
          />
        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}

export default withRouter(SuperAdminDashboard);
